import {
  DataType,
  Form,
  FormDialogMode,
  FormOptions,
  Section,
  useNotify
} from '@globaleris/ui.library'
import { Box, Button, Typography } from '@material-ui/core'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumCommodity, EnumPrecioUnitario } from '../../../../api'
import { PriceCoefficientTable } from '../../../../api/Argentia/endpoints/priceCoefficients/model/PriceCoefficient'
import {
  PriceComponentTypeModel,
  PriceComponentV2RequestModel
} from '../../../../api/Argentia/endpoints/priceComponents'
import {
  EnumPriceCalculationType,
  EnumPriceComponentReference,
  EnumRedondeo
} from '../../../../api/Argentia/enums'
import { EnumPriceType } from '../../../../api/oData/enums/enumPriceType'
import { PriceExpressionValidator } from '../../../../components'
import { PriceCoefficientsCard } from '../../../../components/shared/priceCoefficientsCard/priceCoefficientsCard'
import SimpleDialog from '../../../../components/shared/simpleDialog/simpleDialog'
import { StandardCard } from '../../../../components/shared/standardCard/standardCard'
import {
  enumEnumToValueNumber,
  enumToSelectOptionArray,
  enumToSelectOptionArrayDefaultValues
} from '../../../../helpers'
import { missingFieldsFormWithRequiredText } from '../../../../helpers/validateFormHelper'
import { FormDialogDefinitionProps, useConfirmationDialog } from '../../../../hooks'
import { NonAuthRoutes } from '../../../../model'
import { InformationDialogs } from '../../../../model/enums/informationDialogs'
import { getActiveCompany, getCustomerDomain } from '../../../../redux'
import { createPriceComponentV2 } from '../../../priceComponents/priceComponents.service'
import {
  mapDataToPriceComponentFormData,
  mapFormDataToPriceComponentTypeRequest,
  mapPriceComponentFormDataToRequest,
  mapPricingTableModelToPriceComponentTypeForm
} from '../../pricing.mapper'
import { PricingTableModel } from '../../pricing.Model'
import {
  checkPriceCodeRepeated,
  createPriceComponentType,
  updatePriceComponentType
} from '../../pricing.service'

interface PriceComponentTypeFormProps {
  commodity: number
  priceComponentTypeData?: PricingTableModel
  formMode: FormDialogMode
  priceType?: EnumPriceType
  onClose?: () => void
  updateTableData?: () => void
}

export interface PriceComponentTypeFormData {
  id?: number
  code?: string
  description?: string
  priceType: EnumPriceType
  commodity: EnumCommodity
  referenceType?: EnumPriceComponentReference
  rounding?: EnumRedondeo
  expressionSwitch?: boolean
  idDummyPrice?: number

  unitPrice?: EnumPrecioUnitario
  expression?: string
  priceCalculationType?: EnumPriceCalculationType

  company?: number
  country?: number
}

export interface PriceComponentFormData {
  priceComponentTypeId: number
  priceComponentTypeCode: string
  priceComponentTypeDesc?: string
  code?: string
  description?: string
  priceType: EnumPriceType
  commodity: EnumCommodity
  unitPrice?: EnumPrecioUnitario
  rounding?: EnumRedondeo

  value?: number
  startDate: Date
  endDate?: Date

  period1Value?: number
  period2Value?: number
  period3Value?: number
  period4Value?: number
  period5Value?: number
  period6Value?: number
}

export interface PriceComponentTypeFormRequest extends PriceComponentTypeFormData {
  company: number
  country: number
}

export const PriceComponentTypeForm: React.FC<PriceComponentTypeFormProps> = ({
  commodity,
  priceComponentTypeData,
  formMode,
  priceType,
  onClose,
  updateTableData
}) => {
  const [t] = useTranslation([
    'globalerisLibrary',
    'priceComponentType',
    'enums',
    'sharedTranslations'
  ])
  const history = useHistory()
  const { info, error } = useNotify()
  const { getInformation, closeConfirmation } = useConfirmationDialog()
  const activeCompany = getActiveCompany()

  const isFormEditMode = formMode === FormDialogMode.EDIT
  const isFormAddMode = formMode === FormDialogMode.ADD
  const isLeadOrQuote =
    priceComponentTypeData?.priceComponentTypeReference === EnumPriceComponentReference.Lead ||
    priceComponentTypeData?.priceComponentTypeReference === EnumPriceComponentReference.Oferta

  const [showValidator, setShowValidator] = useState<boolean>(isLeadOrQuote)
  const inputData: PriceComponentTypeFormData | undefined = {
    ...mapPricingTableModelToPriceComponentTypeForm(priceComponentTypeData),
    priceType: priceComponentTypeData?.priceType ?? priceType!,
    commodity,
    expressionSwitch: true
  }
  const [formData, setFormData] = useState<PriceComponentTypeFormData>({ ...inputData })
  const [priceComponentFormData, setPriceComponentFormData] = useState<PriceComponentFormData>()
  const [priceCoefficientsData, setCoefficientsData] = useState<PriceCoefficientTable[]>([])
  const [expressionValidated, setExpressionValidated] = useState<boolean>(false)
  const [modifiedFields, setModifiedFields] = useState<boolean>(false)
  const [codeIsValid, setCodeIsValid] = useState<boolean | undefined>(true)
  const [isCheckingCode, setIsCheckingCode] = useState<boolean>(false)
  const [regexError, setRegexError] = useState<boolean>(false)

  const [showPriceComponentCard, setShowPriceComponentCard] = useState<boolean>(false)
  const [showPeriodValues, setShowPeriodValues] = useState<boolean>(false)
  const [showCoefficients, setShowCoefficients] = useState<boolean>(false)

  // #region Handlers Starts

  // ------- Add PriceComponentType Form Handlers ------- //
  const onExitHandler = async (): Promise<void> => {
    history.goBack()
  }

  const onSaveAndExitHandler = async (): Promise<void> => {
    if (!formData) return

    if (!codeIsValid) {
      error(t('priceComponentType:notifications.checkCode.notValid'))
      return
    }
    if (showValidator && !expressionValidated) {
      error(t('priceComponentType:notifications.create.errorExpression'))
      return
    }
    if (missingFieldsFormWithRequiredText(formSections, formData)) {
      error(t('priceComponentType:notifications.create.errorInvalidForm'))
      return
    }

    const response = await postPriceComponentType(formData)
    if (response) {
      history.push(NonAuthRoutes.pricing)
    }
  }

  const onFormSaveHandler = async (data: PriceComponentTypeFormData): Promise<boolean> => {
    if (data) {
      if (!codeIsValid) {
        error(t('priceComponentType:notifications.checkCode.notValid'))
        return false
      }
      if (showValidator && !expressionValidated) {
        error(t('priceComponentType:notifications.create.errorExpression'))
        return false
      }
      if (missingFieldsFormWithRequiredText(formSections, data)) {
        error(t('priceComponentType:notifications.create.errorInvalidForm'))
        return false
      }
      await postPriceComponentType(data).then(response => {
        // proceed to create priceComponent
        if (response) {
          setShowPriceComponentCard(true)
          formData.priceType === EnumPriceType.ParametroPeriodo && setShowPeriodValues(true)
          formData.priceType === EnumPriceType.Apuntamiento && setShowCoefficients(true)

          const priceComponentInitialData = mapDataToPriceComponentFormData(response.id!, data)
          setPriceComponentFormData(priceComponentInitialData)
        }
      })
      return true
    }
    history.goBack()
    return false
  }

  // ------- Price Expression Validator Handlers ------- //
  const handleValidExpressionSelect = (expression: string | undefined) => {
    showValidator &&
      setFormData(prevState => ({
        ...prevState,
        expression
      }))
  }

  const handlePriceCalculationTypeSelect = (priceCalculationType: EnumPriceCalculationType) => {
    showValidator &&
      setFormData(prevState => ({
        ...prevState,
        priceCalculationType
      }))
  }

  const handlePriceUnitSelect = (unitPrice: EnumPrecioUnitario) => {
    showValidator &&
      setFormData(prevState => ({
        ...prevState,
        unitPrice
      }))
  }

  // ------- Edit PriceComponentType FormDialog Handlers ------- //
  const onEditCloseHandler = async () => {
    if (formData) {
      if (missingFieldsFormWithRequiredText(formSections, formData)) {
        error(t('priceComponentType:notifications.create.errorInvalidForm'))
        return
      }
      await patchPriceComponentType(formData).finally(() => {
        updateTableData && updateTableData()
      })
    }
    onClose && onClose()
  }

  // ------- PriceComponent Add Form Handlers ------- //
  const onPriceComponentFormSaveHandler = async (
    data: PriceComponentFormData
  ): Promise<boolean> => {
    if (data) {
      if (missingFieldsFormWithRequiredText(addPriceComponentFormSections, data)) {
        error(t('priceComponentType:notifications.create.errorInvalidForm'))
        return false
      }

      const request = mapPriceComponentFormDataToRequest(data, priceCoefficientsData)
      await postPriceComponent(request)

      return true
    }
    // Cancel: navigate to pricing list
    history.push(NonAuthRoutes.pricing)
    return false
  }

  // ------- Price Coefficients Card Handlers ------- //
  const handlePriceCoefficientsDataChange = (newState: PriceCoefficientTable[]) => {
    setCoefficientsData(newState)
  }
  // #endregion Handlers Ends

  const isCodeFormatValid = (code: string): boolean => {
    const regex = /^[\w%_áéíóúñÁÉÍÓÚÑ]+$/
    return regex.test(code)
  }

  const validateCode = async (priceCode: string) => {
    if (priceCode === priceComponentTypeData?.priceComponentTypeCode) {
      // No need to validate if code is unchanged.
      setRegexError(false)
      setCodeIsValid(true)

      // setModifiedFields(false)
      return
    }

    if (!isCodeFormatValid(priceCode)) {
      setRegexError(true)
      return
    } else {
      setRegexError(false)
    }

    setIsCheckingCode(true)
    const response = await checkPriceCodeRepeated(priceCode).finally(() => {
      setIsCheckingCode(false)
    })

    if (response.success) {
      if (response.data.isValid) {
        setCodeIsValid(true)
      } else {
        setCodeIsValid(false)
      }
    }
  }

  // #region Functions Starts
  const mapFormRequest = (data: PriceComponentTypeFormData) => {
    const updatedPriceComponentType: PriceComponentTypeFormRequest = {
      ...data,
      company: activeCompany?.id!,
      country: 1
    }
    const request = mapFormDataToPriceComponentTypeRequest(
      updatedPriceComponentType as PriceComponentTypeFormData
    )
    return request
  }

  const postPriceComponentType = async (
    data: PriceComponentTypeFormData
  ): Promise<PriceComponentTypeModel | undefined> => {
    const request = mapFormRequest(data)
    return await createPriceComponentType(request).then(response => {
      if (response.success) {
        info(t('priceComponentType:notifications.create.successPriceComponentType'))
        return response.data
      } else {
        error(t('priceComponentType:notifications.create.errorPriceComponentType'))
        return undefined
      }
    })
  }

  const patchPriceComponentType = async (data: PriceComponentTypeFormData) => {
    const request = mapFormRequest(data)
    return await updatePriceComponentType(request.id!, request)
      .then(response => {
        if (response.success) {
          info(t('priceComponentType:notifications.update.success'))
          history.push(NonAuthRoutes.pricing)
          return true
        } else {
          error(t('priceComponentType:notifications.update.error'))
          return false
        }
      })
      .finally(() => {
        onClose && onClose()
      })
  }

  const postPriceComponent = async (request: PriceComponentV2RequestModel) => {
    return await createPriceComponentV2(request).then(response => {
      if (response.success) {
        info(t('priceComponentType:notifications.create.successPriceComponent'))
        history.push(NonAuthRoutes.pricing)
        return true
      } else {
        error(t('priceComponentType:notifications.create.errorPriceComponent'))
        return false
      }
    })
  }
  // #endregion Functions Ends

  // #region Form Definition Starts

  const defaultPriceTypeOptions: EnumPriceType[] = [
    EnumPriceType.Parametro,
    EnumPriceType.ParametroPeriodo,
    EnumPriceType.Apuntamiento
  ]

  const shouldShowPriceExpressionValidator = (formData: PriceComponentTypeFormData) => {
    return (
      (formData.referenceType === EnumPriceComponentReference.Lead ||
        formData.referenceType === EnumPriceComponentReference.Oferta) &&
      (formData.priceType === EnumPriceType.Parametro ||
        formData.priceType === EnumPriceType.ParametroPeriodo)
    )
  }

  const showPriceExpressionValidator = (formState: PriceComponentTypeFormData) => {
    if (shouldShowPriceExpressionValidator(formState as PriceComponentTypeFormData)) {
      setShowValidator(true)
      setFormData({
        ...formState,
        expressionSwitch: true
      })
    } else {
      setShowValidator(false)
      setFormData(prevState => ({
        ...prevState,
        expression: undefined,
        priceCalculationType: undefined
      }))
    }
  }

  const formSections: Section[] = useMemo(
    () => [
      {
        inputs: [
          {
            name: 'code',
            label: t('priceComponentType:form.add.inputs.code'),
            type: DataType.TEXT,
            maxLength: 56,
            inputSize: isFormEditMode ? 4 : undefined,
            required: true,
            onBlur: value => {
              const code = value.toString()
              validateCode(code)
              setModifiedFields(true)
              setFormData(prevState => ({
                ...prevState,
                code
              }))
            },
            errorState: (!codeIsValid && typeof codeIsValid !== 'undefined') || regexError,
            errorText: !codeIsValid
              ? t('priceComponentType:notifications.checkCode.notValid')
              : t('sharedTranslations:priceCode.errorText')
          },
          {
            name: 'description',
            label: t('priceComponentType:form.add.inputs.description'),
            type: DataType.TEXT,
            inputSize: isFormEditMode ? 8 : undefined,
            maxLength: 200,
            onChange: value => {
              setModifiedFields(true)
            }
          },
          {
            name: 'priceType',
            label: t('priceComponentType:form.add.inputs.priceType'),
            selectOptions: enumToSelectOptionArrayDefaultValues(
              EnumPriceType,
              'enums:EnumPriceTypes',
              defaultPriceTypeOptions
            ),
            required: true,
            type: DataType.SELECT,
            inputSize: isFormEditMode ? 4 : undefined,
            readOnly: isFormEditMode,
            onChange: (value, formState) => {
              showPriceExpressionValidator(formState as PriceComponentTypeFormData)
            }
          },
          {
            name: 'commodity',
            label: t('priceComponentType:form.add.inputs.commodity'),
            selectOptions: enumToSelectOptionArray(EnumCommodity, 'enums:EnumCommodity'),
            type: DataType.SELECT,
            inputSize: isFormEditMode ? 4 : undefined,
            readOnly: isFormEditMode
          },
          {
            name: 'referenceType',
            label: t('priceComponentType:form.add.inputs.referenceType'),
            type: DataType.SELECT,
            selectOptions: enumToSelectOptionArray(
              EnumPriceComponentReference,
              'enums:EnumPriceComponentReference'
            ),
            inputSize: isFormEditMode ? 4 : undefined,
            readOnly: isFormEditMode,
            required: true,
            onChange: (value, formState) => {
              showPriceExpressionValidator(formState as PriceComponentTypeFormData)
            }
          },
          {
            name: 'unitPrice',
            label: t('priceComponentType:form.add.inputs.unitPrice'),
            type: DataType.SELECT,
            selectOptions: enumToSelectOptionArray(EnumPrecioUnitario, 'enums:CurrencyAndUnit'),
            display: isFormAddMode,
            required: true
          },
          {
            name: 'rounding',
            label: t('priceComponentType:form.add.inputs.rounding'),
            type: DataType.SELECT,
            selectOptions: enumToSelectOptionArray(EnumRedondeo, 'enums:EnumRedondeo'),
            required: true,
            display: isFormAddMode
          },
          {
            name: 'expressionSwitch',
            label: t('priceComponentType:form.add.inputs.expressionSwitch'),
            type: DataType.SWITCH,
            display:
              isFormAddMode &&
              formData.referenceType === EnumPriceComponentReference.Lead &&
              formData.priceType !== EnumPriceType.Apuntamiento,
            onChange: (value, formState) => {
              setShowValidator(!!value)
            }
          }
        ]
      }
    ],
    [
      isFormEditMode,
      isFormAddMode,
      codeIsValid,
      regexError,
      formData.referenceType,
      formData.priceType,
      showValidator
    ]
  )

  const saveDisabled =
    !modifiedFields ||
    isCheckingCode ||
    regexError ||
    !codeIsValid ||
    (expressionValidated === false && showValidator)

  const options: FormOptions = {
    elevation: 1,
    columns: 4,
    analytics: {
      customer: getCustomerDomain(),
      view: t('priceComponentType:form.add.analytics')
    }
  }

  const editPriceComponentTypeFormOptions: FormOptions = {
    elevation: 1,
    columns: 4,
    analytics: {
      customer: getCustomerDomain(),
      view: t('priceComponentType:form.add.analytics')
    }
  }

  const editFormDefinition = useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
    return {
      id: 'priceComponentTypeForm',
      sections: formSections
    }
  }, [codeIsValid, regexError, isCheckingCode]) // eslint-disable-line react-hooks/exhaustive-deps

  const addPriceComponentFormSections: Section[] = useMemo(() => {
    const sections: Section[] = [
      {
        title: t('priceComponentType:form.priceComponentFormAdd.sectionTitle1'),
        inputs: [
          {
            name: 'priceComponentTypeCode',
            label: t('priceComponentType:form.add.inputs.code'),
            type: DataType.TEXT,
            readOnly: true
          },
          {
            name: 'priceComponentTypeDesc',
            label: t('priceComponentType:form.add.inputs.description'),
            type: DataType.TEXT,
            readOnly: true
          },
          {
            name: 'priceType',
            label: t('priceComponentType:form.add.inputs.priceType'),
            selectOptions: enumToSelectOptionArrayDefaultValues(
              EnumPriceType,
              'enums:EnumPriceTypes',
              defaultPriceTypeOptions
            ),
            required: true,
            type: DataType.SELECT,
            readOnly: true
          },
          {
            name: 'commodity',
            label: t('priceComponentType:form.add.inputs.commodity'),
            selectOptions: enumToSelectOptionArray(EnumCommodity, 'enums:EnumCommodity'),
            type: DataType.SELECT,
            required: true,
            readOnly: true
          }
        ]
      },
      {
        title: t('priceComponentType:form.priceComponentFormAdd.sectionTitle2'),
        inputs: [
          {
            name: 'code',
            label: t('priceComponentType:form.add.inputs.code'),
            type: DataType.TEXT,
            maxLength: 56,
            required: true,
            onBlur: value => {
              const code = value.toString()
              validateCode(code)
              setModifiedFields(true)
              setPriceComponentFormData(
                prevState =>
                  prevState && {
                    ...prevState,
                    code
                  }
              )
            },
            errorState: (!codeIsValid && typeof codeIsValid !== 'undefined') || regexError,
            errorText: !codeIsValid
              ? t('priceComponentType:notifications.checkCode.notValid')
              : t('sharedTranslations:priceCode.errorText')
          },
          {
            name: 'description',
            label: t('priceComponentType:form.add.inputs.description'),
            type: DataType.TEXT,
            maxLength: 200
          },
          {
            name: 'unitPrice',
            label: t('priceComponentType:form.add.inputs.unitPrice'),
            type: DataType.SELECT,
            selectOptions: enumToSelectOptionArray(EnumPrecioUnitario, 'enums:CurrencyAndUnit'),
            required: true,
            readOnly: true
          },
          {
            name: 'rounding',
            label: t('priceComponentType:form.add.inputs.rounding'),
            type: DataType.SELECT,
            selectOptions: enumToSelectOptionArray(EnumRedondeo, 'enums:EnumRedondeo'),
            readOnly: true
          }
        ]
      }
    ]

    if (!showCoefficients) {
      sections.push({
        title: t('priceComponentType:form.priceComponentFormAdd.sectionTitle3'),
        inputs: [
          {
            name: 'startDate',
            label: t('priceComponentType:form.priceComponentFormAdd.inputs.startDate'),
            type: DataType.DATE,
            required: true
          },
          {
            name: 'value',
            label: t('priceComponentType:form.priceComponentFormAdd.inputs.value'),
            type: DataType.NUMBER,
            display: !showPeriodValues,
            customDecimalScale: priceComponentFormData?.rounding,
            clearable: true
          },
          {
            name: 'period1Value',
            label: t('priceComponentType:form.priceComponentFormAdd.inputs.period1Value'),
            type: DataType.NUMBER,
            display: showPeriodValues,
            customDecimalScale: priceComponentFormData?.rounding,
            clearable: true
          },
          {
            name: 'period2Value',
            label: t('priceComponentType:form.priceComponentFormAdd.inputs.period2Value'),
            type: DataType.NUMBER,
            display: showPeriodValues,
            customDecimalScale: priceComponentFormData?.rounding,
            clearable: true
          },
          {
            name: 'period3Value',
            label: t('priceComponentType:form.priceComponentFormAdd.inputs.period3Value'),
            type: DataType.NUMBER,
            display: showPeriodValues,
            customDecimalScale: priceComponentFormData?.rounding,
            clearable: true
          },
          {
            name: 'period4Value',
            label: t('priceComponentType:form.priceComponentFormAdd.inputs.period4Value'),
            type: DataType.NUMBER,
            display: showPeriodValues,
            customDecimalScale: priceComponentFormData?.rounding,
            clearable: true
          },
          {
            name: 'period5Value',
            label: t('priceComponentType:form.priceComponentFormAdd.inputs.period5Value'),
            type: DataType.NUMBER,
            display: showPeriodValues,
            customDecimalScale: priceComponentFormData?.rounding,
            clearable: true
          },
          {
            name: 'period6Value',
            label: t('priceComponentType:form.priceComponentFormAdd.inputs.period6Value'),
            type: DataType.NUMBER,
            display: showPeriodValues,
            customDecimalScale: priceComponentFormData?.rounding,
            clearable: true
          }
        ]
      })
    }
    return sections
  }, [
    codeIsValid,
    regexError,
    priceComponentFormData?.code,
    modifiedFields,
    showCoefficients,
    showPeriodValues,
    priceComponentFormData?.rounding
  ])

  const priceComponentFormDataOptions: FormOptions = {
    elevation: 0,
    columns: 4,
    analytics: {
      customer: getCustomerDomain(),
      view: t('priceComponentType:form.priceComponentFormAdd.analytics')
    },
    saveDisabled: !modifiedFields || isCheckingCode || regexError || !codeIsValid,
    onSave: data => onPriceComponentFormSaveHandler(data)
  }
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  // #endregion Hooks Ends

  // Conditional logic for Oferta type
  const isOfertaType = formData.referenceType === EnumPriceComponentReference.Oferta
  const shouldShowAddBaseValue = !isOfertaType

  return (
    <>
      {isFormAddMode && !showPriceComponentCard && (
        <>
          <StandardCard style={{ margin: '0 0 24px 0' }} raised={false}>
            <Box display="flex" justifyContent="flex-end" px={2} pt={2} pb={2} gridGap={4}>
              <Button size="small" variant="text" color="primary" onClick={onExitHandler}>
                {t('priceComponentType:mainActions.cancel')}
              </Button>
              {shouldShowAddBaseValue && (
                <Button
                  size="small"
                  variant="text"
                  color="primary"
                  disabled={saveDisabled}
                  onClick={() => onFormSaveHandler(formData)}
                >
                  {t('priceComponentType:mainActions.addBaseValue')}
                </Button>
              )}
              <Button
                size="small"
                variant="contained"
                color="primary"
                disabled={saveDisabled}
                onClick={onSaveAndExitHandler}
              >
                {t('priceComponentType:mainActions.save')}
              </Button>
            </Box>
            <Form sections={formSections} data={formData} options={{ ...options, elevation: 0 }} />
          </StandardCard>
          <PriceExpressionValidator
            title={t('priceComponentType:form.add.priceExpressionValidator')}
            show={showValidator}
            priceType={formData.priceType ?? priceType!}
            commodity={commodity}
            onIsValid={isValid => {
              setExpressionValidated(isValid)
            }}
            onSelectValidExpression={handleValidExpressionSelect}
            onSelectPriceCalculationType={handlePriceCalculationTypeSelect}
            onSelectPriceUnit={handlePriceUnitSelect}
            priceVersion={1}
            activePriceUnit={enumEnumToValueNumber(EnumPrecioUnitario, formData?.unitPrice)}
          />
        </>
      )}
      {isFormEditMode && onClose && (
        <SimpleDialog
          open={isFormEditMode}
          handleClose={onClose}
          style={{ padding: '0 8px 0px 8px' }}
          footerActions={[
            {
              label: t('globalerisLibrary:form.actions.cancel'),
              color: 'secondary',
              handleActionClick: onClose
            },
            {
              label: t('globalerisLibrary:form.actions.save'),
              color: 'primary',
              disabled: !modifiedFields || isCheckingCode || regexError || !codeIsValid,
              handleActionClick: onEditCloseHandler
            }
          ]}
        >
          <Form
            titleCustom={
              <Typography variant="h5" color="secondary">
                {t('priceComponentType:form.edit.title')}
              </Typography>
            }
            sections={formSections}
            data={formData}
            options={{ ...editPriceComponentTypeFormOptions, elevation: 0 }}
          />
        </SimpleDialog>
      )}

      {showPriceComponentCard && (
        <StandardCard style={{ margin: '0 0 24px 0' }} raised={false}>
          <Form
            sections={[...addPriceComponentFormSections]}
            data={{ ...priceComponentFormData }}
            options={{ ...priceComponentFormDataOptions }}
          />
        </StandardCard>
      )}
      {showCoefficients && (
        <PriceCoefficientsCard
          rounding={formData.rounding ?? 6}
          onTableDataChange={handlePriceCoefficientsDataChange}
          style={{ margin: '0 0 24px 0' }}
          raised={false}
        />
      )}
    </>
  )
}
